/**
 * Retention and erasure.
 *
 * Mirrors `apps/api/src/modules/crm-marketing/retention.routes.ts`. Two things
 * that look like settings and are not: turning the switch on starts deleting
 * data that does not come back, and the erase call removes one person on
 * request under article 17. Both are behind `manage:crm.marketing`; reading the
 * numbers is not.
 */
import { api } from "./api";

export interface RetentionSettings {
  /** Off by default. A deploy must never be the thing that starts deleting. */
  enabled: boolean;
  /** Days after a letter went out before the ledger row is stripped. */
  recipientDays: number;
  /** Days after the last contact before the address memory is deleted. */
  contactDays: number;
}

/** What a sweep at the current ages would remove right now. */
export interface RetentionDue {
  /** Ledger rows that would lose the mailbox and the name. The row survives. */
  recipients: number;
  /** Address memories that would be deleted outright. */
  contacts: number;
  /** Campaign threads nobody answered, letters and all. */
  threads: number;
  /**
   * The contact age actually in force, after the clamp against the longest
   * re-contact window. Not always the number in the box.
   */
  contactDaysInForce: number;
  /** True when the clamp moved it. The card has to say so and say why. */
  clamped: boolean;
}

export interface RetentionView {
  settings: RetentionSettings;
  due: RetentionDue;
  limits: { min: number; max: number };
  /** What the clamp is measured against. */
  longestRecontactDays: number;
}

/** What one erasure removed, and what it deliberately did not. */
export interface EraseReport {
  emailKey: string;
  wasSuppressed: boolean;
  recipients: number;
  contacts: number;
  threads: number;
  /** Threads this person replied to. Left alone; a human decides on those. */
  threadsWithReply: number;
  /** Register-mirror rows carrying the address. Not ours to delete. */
  registerRows: number;
}

const BASE = "/admin/crm/marketing";

export const getRetention = (): Promise<RetentionView> => api.get(`${BASE}/retention`);

export const saveRetention = (body: Partial<RetentionSettings>): Promise<{
  settings: RetentionSettings;
  due: RetentionDue;
}> => api.put(`${BASE}/retention`, body);

/** What OTHER ages would remove. Writes nothing; the switch is not consulted. */
export const previewRetention = (body: { recipientDays: number; contactDays: number }): Promise<{
  due: RetentionDue;
}> => api.post(`${BASE}/retention/preview`, body);

/** One address, on request. The reason is stored on the suppression row. */
export const eraseAddress = (body: { email: string; reason: string }): Promise<{ report: EraseReport }> =>
  api.post(`${BASE}/erase`, body);
